'use client'

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type {  } from 'react'
import type { ReactNode } from 'react'
import { withOperationsDemoFetchInit } from '@/lib/demo/clientContext'
import type {  } from '@/lib/demo/clientContext'
import { getCurrentTheme, setTheme as applyTheme, initializeTheme, applyClientBranding } from '@/lib/themes'
import type { Theme } from '@/lib/themes'
import { MARKETING_PALETTE } from '@/lib/marketingPalette'
import { isAppShellRoute } from '@/lib/platformUtils'

// Re-exported for client components. Server components should import from
// '@/lib/marketingPalette' directly — this module is 'use client'.
export { MARKETING_PALETTE } from '@/lib/marketingPalette'

const MANAGED_FAVICON_ATTR = 'data-totalaccess-client-favicon'

interface ClientSwitchEventDetail {
  activeClient?: {
    logo_url?: string | null
  } | null
}

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

interface ClientBranding {
  primary_color?: string
  secondary_color?: string
  header_color?: string
  primaryColor?: string
  secondaryColor?: string
  headerColor?: string
  logo_url?: string
  trading_name?: string
  favicon_url?: string
}

/**
 * Standard action blue (Tailwind blue-600) — the fixed, brand-agnostic accent
 * for primary/action buttons and selected items in forms and modals.
 * Intentionally NOT driven by client branding or the selectable theme, so
 * interactive affordances stay consistent for every client.
 */
export const ACTION_BLUE = '#2563eb'

interface ThemeContextType {
  currentTheme: Theme
  isDark: boolean
  setTheme: (themeId: number) => void
  toggleThemeMode: () => void
  currentLogo: string
  clientLogo: string | null
  setClientLogo: (logo: string | null) => void
  clientBranding: ClientBranding | null
  setClientBranding: (branding: ClientBranding | null) => void
  applyBrandingColors: (primaryColor: string, secondaryColor: string) => void
  headerColor: string | null
  setHeaderColor: (color: string | null) => void
  // Convenience getters for consistent color usage
  primaryColor: string       // client branding primary OR theme primary
  secondaryColor: string     // client branding secondary OR theme secondary
  brandingHeaderColor: string // header color OR primary color (for headers/nav)
  actionColor: string        // fixed ACTION_BLUE — for action buttons & selected items; never overridden by client branding
  /** Fixed public marketing-site palette — never overridden by client branding. */
  marketingPalette: typeof MARKETING_PALETTE
  /** Set tab favicon (e.g. custom-domain whitelabel before/without auth). Uses managed link tags only. */
  applyClientFavicon: (faviconUrl: string | null | undefined) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Always start with default theme to avoid hydration mismatch
  const defaultTheme = typeof window === 'undefined' ? getCurrentTheme() : getCurrentTheme()
  const [currentTheme, setCurrentThemeState] = useState<Theme>(defaultTheme)
  const [currentLogo, setCurrentLogo] = useState<string>(defaultTheme.logo)
  const [clientLogo, setClientLogoState] = useState<string | null>(null)
  const [clientBranding, setClientBrandingState] = useState<ClientBranding | null>(null)
  const [headerColor, setHeaderColorState] = useState<string | null>(null)

  // Apply theme to document - defined before useEffect that uses it
  const applyThemeToDocument = useCallback((theme: Theme) => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      const body = document.body

      // Apply CSS variables
      root.style.setProperty('--theme-primary', theme.colors.primary)
      root.style.setProperty('--theme-primary-dark', theme.colors.primaryDark)
      root.style.setProperty('--theme-secondary', theme.colors.secondary)
      root.style.setProperty('--theme-secondary-dark', theme.colors.secondaryDark)
      root.style.setProperty('--theme-accent', theme.colors.accent)
      root.style.setProperty('--theme-background', theme.colors.background)
      root.style.setProperty('--theme-surface', theme.colors.surface)
      root.style.setProperty('--theme-surface-dark', theme.colors.surfaceDark)

      // Set text and border colors based on theme mode
      if (theme.mode === 'dark') {
        root.style.setProperty('--theme-text-primary', '#0b1b2b')
        root.style.setProperty('--theme-text-secondary', '#04204b')
        root.style.setProperty('--theme-border', '#042e72')
        root.style.setProperty('--theme-border-dark', '#154891')
      } else {
        root.style.setProperty('--theme-text-primary', '#111827')
        root.style.setProperty('--theme-text-secondary', '#051127')
        root.style.setProperty('--theme-border', '#172030')
        root.style.setProperty('--theme-border-dark', '#1b1d20')
      }

      // Update RGB values for gradient effects
      const primaryRgb = hexToRgb(theme.colors.primary)
      if (primaryRgb) {
        root.style.setProperty('--primary-blue-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`)
        root.style.setProperty('--neon-primary', `0 0 20px rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.5)`)
        root.style.setProperty('--neon-blue', `0 0 20px rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.5)`)
      }

      // Apply body background
      body.style.backgroundColor = theme.colors.background

      // Apply theme class to body for broader styling control
      body.className = body.className.replace(/theme-\w+/g, '').trim()
      body.classList.add(`theme-${theme.id}`)

      // Apply theme mode class
      body.className = body.className.replace(/theme-mode-\w+/g, '').trim()
      body.classList.add(`theme-mode-${theme.mode}`)

      // Add/remove Tailwind dark class for Tailwind CSS dark mode support
      if (theme.mode === 'dark') {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }

      // Set data attributes for advanced styling
      root.setAttribute('data-theme', theme.id.toString())
      root.setAttribute('data-theme-mode', theme.mode)
    }
  }, [])

  const clearManagedFavicons = useCallback(() => {
    if (typeof document === 'undefined') return

    document.head
      .querySelectorAll(`link[${MANAGED_FAVICON_ATTR}="true"]`)
      .forEach((node) => node.remove())
  }, [])

  // Helper function to update the browser favicon - defined before loadClientBranding
  const applyFavicon = useCallback((faviconUrl: string) => {
    if (typeof document === 'undefined' || !faviconUrl) return

    const upsertFaviconLink = (rel: string, type?: string) => {
      const selector = `link[${MANAGED_FAVICON_ATTR}="true"][rel="${rel}"]`
      let link = document.head.querySelector(selector) as HTMLLinkElement | null

      if (!link) {
        link = document.createElement('link')
        link.rel = rel
        link.setAttribute(MANAGED_FAVICON_ATTR, 'true')
        document.head.appendChild(link)
      }

      if (type) {
        link.type = type
      } else {
        link.removeAttribute('type')
      }

      link.href = faviconUrl
    }

    // Only manage our own client-branding favicon tags.
    // Do NOT remove Next.js-managed metadata icons from <head>, because
    // React still tracks those nodes and will crash on navigation if they
    // disappear behind its back.
    upsertFaviconLink('icon', 'image/x-icon')
    upsertFaviconLink('shortcut icon')
    upsertFaviconLink('apple-touch-icon')
  }, [])

  const applyClientFavicon = useCallback((faviconUrl: string | null | undefined) => {
    if (faviconUrl) {
      applyFavicon(faviconUrl)
      return
    }

    clearManagedFavicons()
  }, [applyFavicon, clearManagedFavicons])

  // Apply branding colors - defined before loadClientBranding
  const applyBrandingColors = useCallback((primaryColor: string, secondaryColor: string) => {
    if (typeof document !== 'undefined') {
      applyClientBranding(primaryColor, secondaryColor)
    }
  }, [])

  const clearClientBrandingState = useCallback((optimisticLogoUrl: string | null = null) => {
    setClientBrandingState(null)
    setHeaderColorState(null)
    setClientLogoState(optimisticLogoUrl)
    clearManagedFavicons()
    applyThemeToDocument(currentTheme)
  }, [applyThemeToDocument, clearManagedFavicons, currentTheme])

  // Load client branding - defined after functions it depends on
  const loadClientBranding = useCallback(async ({ optimisticLogoUrl }: { optimisticLogoUrl?: string | null } = {}) => {
    const normalizedOptimisticLogo = typeof optimisticLogoUrl === 'string' && optimisticLogoUrl.trim().length > 0
      ? optimisticLogoUrl
      : null

    clearClientBrandingState(normalizedOptimisticLogo)

    try {
      // Marketing pages always render in the fixed marketing palette — skip
      // the authed branding call on public routes entirely.
      if (!isAppShellRoute(window.location.pathname)) {
        return
      }

      // Get auth tokens from correct localStorage key
      const authTokensStr = localStorage.getItem('auth_tokens')
      if (!authTokensStr) {
        console.log('[ThemeContext] No auth tokens available yet, skipping branding load')
        return
      }
      
      const authTokens = JSON.parse(authTokensStr)
      const accessToken = authTokens?.access_token
      
      if (!accessToken) {
        console.log('[ThemeContext] No access token found, skipping branding load')
        return
      }
      
      const response = await fetch('/api/v1/whitelabel/branding', withOperationsDemoFetchInit({
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }))
      
      if (response.ok) {
        const { data } = await response.json()
        if (data?.primary_color && data?.secondary_color) {
          // Map whitelabel_header_color to header_color for ClientBranding interface
          const brandingData: ClientBranding = {
            primary_color: data.primary_color,
            secondary_color: data.secondary_color,
            header_color: data.whitelabel_header_color || undefined,
            primaryColor: data.primary_color,
            secondaryColor: data.secondary_color,
            headerColor: data.whitelabel_header_color || undefined,
            logo_url: data.logo_url,
            trading_name: data.trading_name,
            favicon_url: data.favicon_url
          }
          setClientBrandingState(brandingData)
          applyBrandingColors(data.primary_color, data.secondary_color)
        } else {
          setClientBrandingState(null)
          applyThemeToDocument(currentTheme)
        }

        setHeaderColorState(data?.whitelabel_header_color || null)
        setClientLogoState(data?.logo_url || normalizedOptimisticLogo)
        applyClientFavicon(data?.favicon_url || null)
      } else if (response.status === 401) {
        console.log('[ThemeContext] Auth token invalid/expired, skipping branding (expected during OAuth)')
      } else if (response.status === 404) {
        console.log('[ThemeContext] Client branding not configured for the current context, using theme defaults')
      }
    } catch (error) {
      console.log('[ThemeContext] No client branding found, using theme defaults')
    }
  }, [applyBrandingColors, applyClientFavicon, applyThemeToDocument, clearClientBrandingState, currentTheme])

  // Initialize theme and load branding on mount
  useEffect(() => {
    // Initialize theme on mount (client-side only)
    initializeTheme()
    const theme = getCurrentTheme()
    applyThemeToDocument(theme)

    // Load client branding if available
    const brandingFrame = window.requestAnimationFrame(() => {
      void loadClientBranding()
    })

    // Listen for OS color scheme changes (only if user hasn't set explicit preference)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleOSThemeChange = (e: MediaQueryListEvent) => {
      // Only respond to OS changes if user hasn't set an explicit app preference
      const hasExplicitPreference = localStorage.getItem('app_theme') !== null
      if (!hasExplicitPreference) {
        const newTheme = getCurrentTheme() // Will now return OS-based theme
        setCurrentThemeState(newTheme)
        setCurrentLogo(newTheme.logo)
        applyThemeToDocument(newTheme)
        console.log('[ThemeContext] OS theme changed, updated to:', newTheme.id, 'mode:', newTheme.mode)
      }
    }

    mediaQuery.addEventListener('change', handleOSThemeChange)

    return () => {
      window.cancelAnimationFrame(brandingFrame)
      mediaQuery.removeEventListener('change', handleOSThemeChange)
    }
  }, [applyThemeToDocument, loadClientBranding])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleClientContextChanged = (event: Event) => {
      const customEvent = event as CustomEvent<ClientSwitchEventDetail>
      const nextLogo = typeof customEvent.detail?.activeClient?.logo_url === 'string'
        ? customEvent.detail.activeClient.logo_url
        : null

      void loadClientBranding({ optimisticLogoUrl: nextLogo })
    }

    window.addEventListener('client-switched', handleClientContextChanged as EventListener)
    window.addEventListener('activeClientChanged', handleClientContextChanged as EventListener)

    return () => {
      window.removeEventListener('client-switched', handleClientContextChanged as EventListener)
      window.removeEventListener('activeClientChanged', handleClientContextChanged as EventListener)
    }
  }, [loadClientBranding])

  const setTheme = (themeId: number) => {
    applyTheme(themeId)
    const theme = getCurrentTheme()
    setCurrentThemeState(theme)
    setCurrentLogo(theme.logo)
    applyThemeToDocument(theme)
  }

  const toggleThemeMode = () => {
    const newThemeId = currentTheme.mode === 'light'
      ? currentTheme.id + 1
      : currentTheme.id - 1

    if (newThemeId >= 1 && newThemeId <= 20) {
      setTheme(newThemeId)
    }
  }

  const setClientBranding = (branding: ClientBranding | null) => {
    setClientBrandingState(branding)
    if (branding?.primary_color && branding?.secondary_color) {
      applyBrandingColors(branding.primary_color, branding.secondary_color)
    } else {
      applyThemeToDocument(currentTheme)
    }
    setHeaderColorState(branding?.header_color || null)
    setClientLogoState(branding?.logo_url || null)
    applyClientFavicon(branding?.favicon_url || null)
  }

  const setClientLogo = (logo: string | null) => {
    setClientLogoState(logo)
    console.log('[ThemeContext] Client logo updated:', logo)
  }

  const setHeaderColor = (color: string | null) => {
    setHeaderColorState(color)
    console.log('[ThemeContext] Header color updated:', color)
  }

  // Computed convenience properties for consistent color usage across components
  // primaryColor: Use client branding primary, fallback to theme primary
  const primaryColor = clientBranding?.primary_color || currentTheme.colors.primary

  // secondaryColor: Use client branding secondary, fallback to theme secondary
  const secondaryColor = clientBranding?.secondary_color || currentTheme.colors.secondary

  // brandingHeaderColor: Use header color if set, otherwise use primary color (for nav/headers)
  const brandingHeaderColor = headerColor || clientBranding?.header_color || primaryColor
  const isDark = currentTheme.mode === 'dark'

  return (
    <ThemeContext.Provider value={{ 
      currentTheme, 
      isDark,
      setTheme, 
      toggleThemeMode, 
      currentLogo,
      clientLogo,
      setClientLogo,
      clientBranding,
      setClientBranding,
      applyBrandingColors,
      headerColor,
      setHeaderColor,
      // Convenience getters
      primaryColor,
      secondaryColor,
      brandingHeaderColor,
      actionColor: ACTION_BLUE,
      marketingPalette: MARKETING_PALETTE,
      applyClientFavicon
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
